// Lib
import React, { DetailedHTMLProps, HTMLAttributes, useCallback, useMemo } from 'react';
import { NodeViewProps } from '@tiptap/core';
import { getColorCssValue } from '../../utils/elementColorStyleUtils';
import { useDispatch, useSelector } from 'react-redux';
import { getElement } from '../../selectors/elementSelector';
import { getColor, getTitle } from '../../../../common/elements/utils/elementPropertyUtils';
import useSyncInlineBoardLinkDetails from '../../../../common/tiptap/extensions/inlineBoardLink/useSyncInlineBoardLinkDetails';
import { InlineBoardLinkOptions } from '../../../../common/tiptap/extensions/inlineBoardLink/inlineBoardLinkConstants';
import { navigateToBoard } from '../../../reducers/navigationActions';
import { stopPropagationOnly } from '../../../utils/domUtil';
import { hasCommandModifier } from '../../../utils/keyboard/keyboardUtility';
import Icon from '../../../components/icons/Icon';

type StyleProps = DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>['style'];

const InlineBoardLinkResolvedContent: React.FC<NodeViewProps> = (props) => {
    const { node, extension, view, getPos, editor } = props;

    const { clickableLinksWhenEditable, dispatchFetchInlineBoardLinkDetails } =
        extension.options as InlineBoardLinkOptions;

    useSyncInlineBoardLinkDetails({
        dispatchFetchInlineBoardLinkDetails,
        attrs: node.attrs,
        editor,
        getPos,
    });

    const boardId = node.attrs.boardId || '';

    const boardColor = useSelector((state) => getColor(getElement(state, { elementId: boardId })));
    const boardTitle = useSelector((state) => getTitle(getElement(state, { elementId: boardId })));

    const renderedColor = boardColor || node.attrs.color;

    const dispatch = useDispatch();
    const dispatchNavigateToBoard = useCallback((boardId: string, newTab: boolean) => {
        dispatch(navigateToBoard({ boardId, navigationSource: undefined, newTab }));
    }, []);

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            if (view.editable && !clickableLinksWhenEditable) {
                // Select the link node when clicked
                editor.chain().setNodeSelection(getPos()).focus().run();
                return;
            }

            const shouldOpenInNewTab = hasCommandModifier(event);

            dispatchNavigateToBoard(boardId, shouldOpenInNewTab);
        },
        [view.editable, clickableLinksWhenEditable],
    );

    // @ts-ignore CSS custom properties fail the type check
    const style: StyleProps = useMemo(() => ({ '--board-color': getColorCssValue(renderedColor) }), [renderedColor]);

    return (
        <span
            className="pill"
            onMouseDown={stopPropagationOnly}
            onClick={handleClick}
            data-board-id={boardId}
            data-permission-id={node.attrs.permissionId || undefined}
        >
            <span className="board-icon-wrapper" style={style}>
                <Icon name="editor-inline-board" />
            </span>
            <span className="board-title">{boardTitle || node.attrs.title || 'Unknown board'}</span>
        </span>
    );
};

export default InlineBoardLinkResolvedContent;
